import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../utils/auth';
import { getUserPreferences, updateUserPreferences } from '../utils/userPreferences';
import i18n from '../i18n/config';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const supportedLanguages = {
  en: { code: 'en', name: 'English', nativeName: 'English' },
  es: { code: 'es', name: 'Español', nativeName: 'Español' },
  fr: { code: 'fr', name: 'Français', nativeName: 'Français' },
  de: { code: 'de', name: 'Deutsch', nativeName: 'Deutsch' },
  it: { code: 'it', name: 'Italiano', nativeName: 'Italiano' },
  pt: { code: 'pt', name: 'Português', nativeName: 'Português' },
  ru: { code: 'ru', name: 'Русский', nativeName: 'Русский' },
  'zh-CN': { code: 'zh-CN', name: '中文', nativeName: '中文' },
  ja: { code: 'ja', name: '日本語', nativeName: '日本語' },
  ko: { code: 'ko', name: '한국어', nativeName: '한국어' },
  ar: { code: 'ar', name: 'العربية', nativeName: 'العربية' },
  hi: { code: 'hi', name: 'हिन्दी', nativeName: 'हिन्दी' },
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Load from localStorage immediately (synchronous) for instant display
  const getInitialLanguage = () => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && supportedLanguages[savedLanguage]) {
      return savedLanguage;
    }
    return 'en';
  };
  
  const [currentLanguage, setCurrentLanguage] = useState(getInitialLanguage);
  const [loading, setLoading] = useState(false); // Start as false since we load from localStorage immediately

  // Initialize i18n with localStorage language immediately
  useEffect(() => {
    const initialLang = getInitialLanguage();
    if (i18n.language !== initialLang) {
      i18n.changeLanguage(initialLang);
    }
  }, []);

  // Sync with database in background (non-blocking)
  useEffect(() => {
    const syncWithDatabase = async () => {
      if (!isAuthenticated()) {
        setLoading(false);
        return;
      }

      try {
        const preferences = await getUserPreferences();
        if (preferences?.language && supportedLanguages[preferences.language]) {
          // Only update if different from localStorage
          if (preferences.language !== currentLanguage) {
            setCurrentLanguage(preferences.language);
            localStorage.setItem('language', preferences.language);
            await i18n.changeLanguage(preferences.language);
          }
        } else {
          // Save current localStorage value to database
          const savedLanguage = localStorage.getItem('language') || 'en';
          if (savedLanguage && supportedLanguages[savedLanguage]) {
            await updateUserPreferences({ language: savedLanguage });
          }
        }
      } catch (error) {
        console.error('Error syncing language preference with database:', error);
        // Continue with localStorage value
      } finally {
        setLoading(false);
      }
    };

    syncWithDatabase();
  }, []); // Only run once on mount

  const changeLanguage = async (langCode) => {
    if (!supportedLanguages[langCode]) return;
    
    // Update immediately (synchronous)
    setCurrentLanguage(langCode);
    localStorage.setItem('language', langCode);
    
    // Update i18next language immediately
    i18n.changeLanguage(langCode).catch(err => {
      console.error('Error changing i18n language:', err);
    });

    // Save to database in background (non-blocking)
    if (isAuthenticated()) {
      updateUserPreferences({ language: langCode }).catch(error => {
        console.error('Error saving language preference to database:', error);
        // Continue anyway - localStorage is already updated
      });
    }
    
    // Notify other components
    window.dispatchEvent(new CustomEvent('languagechange', { detail: { language: langCode } }));
  };

  const getLocalizedPath = (path) => {
    // Return path as-is (no language prefix)
    return path;
  };

  // Convert supportedLanguages object to array for easier use in components
  const supportedLanguagesArray = Object.values(supportedLanguages);

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        changeLanguage,
        supportedLanguages: supportedLanguagesArray,
        supportedLanguagesMap: supportedLanguages,
        getLocalizedPath,
        currentLanguageData: supportedLanguages[currentLanguage] || supportedLanguages.en,
        loading,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

